import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, parseISO } from "date-fns";
import { Lock, Globe, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ReportFieldInput, displayValue } from "./ReportFieldInput";
import { StatusChip } from "./StatusChip";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy · HH:mm"); } catch { return d; } };

export function ReportDetailDialog({ report, onClose, templates, perms, onChanged }) {
  const [comment, setComment] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [statusNote, setStatusNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setComment(""); setEditing(false); setStatusNote(""); }, [report?.id]);
  if (!report) return null;
  const fields = templates?.[report.type] || [];
  const labels = perms?.status_labels || {};

  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); onChanged?.(data); if (ok) toast.success(ok); return data; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!report} onOpenChange={v => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="report-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-3 flex-wrap">
            {report.type === "accident" ? "Accident" : "Incident"} {report.ref} <StatusChip status={report.status} labels={labels} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="text-xs opacity-60">Reported by <strong>{report.reporter_name}</strong> · {fmt(report.created_at)}{report.team_name ? ` · ${report.team_name}` : ""}</div>

          {report.can_review && (
            <div className="p-3 rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 space-y-2" data-testid="report-status-panel">
              <div className="text-[10px] tracking-widest uppercase opacity-70">Review stage</div>
              <div className="flex gap-2 flex-wrap items-start">
                <Select value={report.status} onValueChange={v => run(() => api.post(`/incidents/${report.id}/status`, { status: v, note: statusNote || null }), `Moved to ${labels[v] || v} — reporter alerted`)}>
                  <SelectTrigger data-testid="report-status-select" className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>{(perms?.statuses || []).map(s => <SelectItem key={s} value={s}>{labels[s] || s}</SelectItem>)}</SelectContent>
                </Select>
                <Textarea rows={1} value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Optional note sent with the stage change" data-testid="report-status-note" className="flex-1 min-w-[200px]" />
              </div>
            </div>
          )}

          <div className="rounded-md border border-border">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <div className="text-[10px] tracking-widest uppercase opacity-70">Report details</div>
              {report.status !== "closed" && !editing && (
                <button type="button" onClick={() => { setEdit({ ...report.fields }); setEditing(true); }} data-testid="report-edit-btn" className="text-[10px] tracking-widest uppercase inline-flex items-center gap-1 opacity-70 hover:opacity-100"><Pencil className="w-3 h-3" /> Amend</button>
              )}
            </div>
            {editing ? (
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map(f => <ReportFieldInput key={f.key} field={f} value={edit[f.key]} onChange={v => setEdit(c => ({ ...c, [f.key]: v }))} />)}
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setEditing(false)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border">Cancel</button>
                  <button type="button" disabled={busy} data-testid="report-edit-save" onClick={async () => { const d = await run(() => api.put(`/incidents/${report.id}`, { fields: edit }), "Report amended — everyone alerted"); if (d) setEditing(false); }} className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">Save changes</button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 p-3" data-testid="report-fields">
                {fields.map(f => (
                  <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <dt className="text-[10px] tracking-widest uppercase opacity-50">{f.label}</dt>
                    <dd className="whitespace-pre-wrap mt-0.5" data-testid={`report-value-${f.key}`}>{displayValue(f, report.fields?.[f.key])}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div>
            <div className="text-[10px] tracking-widest uppercase opacity-70 mb-2">Comments & review notes</div>
            <div className="space-y-2" data-testid="report-comments">
              {(report.comments || []).length === 0 && <div className="text-xs opacity-50">No comments yet.</div>}
              {(report.comments || []).map(c => (
                <div key={c.id} className={`p-3 rounded-md border text-xs ${c.is_public ? "border-border" : "border-amber-400/40 bg-amber-500/5"}`} data-testid={`report-comment-${c.id}`}>
                  <div className="flex items-center gap-2 opacity-70 mb-1">
                    <strong>{c.author_name}</strong> · {fmt(c.at)}
                    {c.author_role === "reviewer" && (c.is_public ? <span className="inline-flex items-center gap-1 text-[#83B68B]"><Globe className="w-3 h-3" /> visible to reporter</span> : <span className="inline-flex items-center gap-1 text-amber-300"><Lock className="w-3 h-3" /> private — reviewers only</span>)}
                  </div>
                  <div className="whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-2">
              <Textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder={report.can_review ? "Add a review comment…" : "Add further information for the reviewers…"} data-testid="report-comment-input" />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {report.can_review ? (
                  <div className="inline-flex rounded-full border border-border overflow-hidden text-[10px] tracking-widest uppercase" data-testid="report-comment-visibility">
                    <button type="button" onClick={() => setIsPublic(true)} data-testid="report-comment-public" className={`px-3 py-1.5 inline-flex items-center gap-1 ${isPublic ? "bg-[#83B68B] text-white" : "opacity-60"}`}><Globe className="w-3 h-3" /> Public</button>
                    <button type="button" onClick={() => setIsPublic(false)} data-testid="report-comment-private" className={`px-3 py-1.5 inline-flex items-center gap-1 ${!isPublic ? "bg-amber-500 text-black" : "opacity-60"}`}><Lock className="w-3 h-3" /> Private</button>
                  </div>
                ) : <span />}
                <button type="button" disabled={busy || !comment.trim()} data-testid="report-comment-submit" onClick={async () => { const d = await run(() => api.post(`/incidents/${report.id}/comments`, { text: comment, is_public: isPublic }), "Comment added"); if (d) setComment(""); }} className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">Post comment</button>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-widest uppercase opacity-70 mb-1">Stage history</div>
            <ol className="text-xs space-y-1 opacity-80" data-testid="report-history">
              {(report.status_history || []).map((h, i) => <li key={`${h.at}-${i}`}>{fmt(h.at)} · <strong>{labels[h.status] || h.status}</strong> by {h.by_name}{h.note ? ` — ${h.note}` : ""}</li>)}
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
